import React, { useState } from 'react';
import './Modal.css';
import { Form } from 'react-bootstrap';
import { useAddFileFournisseursMutation } from 'actions/importer_fichier/mutations';
import { toast } from 'react-toastify';
import { useNavigate, useParams } from 'react-router-dom';
import FileUpload from './File/FileUpload';

const COLONNES = [
  {
    label: 'NOM',
    required: true,
    description: 'Nom du fournisseur',
    format: null,
    example: 'Dupont SARL',
  },
  {
    label: 'TELEPHONE',
    required: false,
    description: 'Numéro de téléphone',
    format: '+ autorisé en début · chiffres uniquement',
    example: '+221771234567 ou 771234567',
  },
  {
    label: 'ADRESSE',
    required: false,
    description: 'Adresse du fournisseur',
    format: null,
    example: '12 rue Carnot, Dakar',
  },
  {
    label: 'EMAIL',
    required: false,
    description: 'Adresse email',
    format: 'Format : nom@domaine.ext',
    example: 'contact@example.com',
  },
  {
    label: 'LIBELLE',
    required: false,
    description: 'Libellé comptable',
    format: 'Défaut : FOURNISSEURS',
    example: 'FOURNISSEURS',
  },
];

const ModalExcel = ({ isOpen, onClose, refetch, retour }) => {
  const { clientID: id } = useParams();
  const navigate = useNavigate();

  const [selectedFile, setSelectedFile] = useState(null);
  const [selected, setSelected] = useState(false);
  const [erreurs, setErreurs] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const [addFile] = useAddFileFournisseursMutation();

  const handleFileSelect = (file) => {
    setSelectedFile(file);
    setSelected(true);
    setErreurs([]);
  };

  const handleClose = () => {
    setSelectedFile(null);
    setSelected(false);
    setErreurs([]);
    onClose();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!selectedFile) return;

    setErreurs([]);
    setIsLoading(true);

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('dossier_id', id);

    try {
      await addFile({ dossier: id, formData }).unwrap();
      toast.success('Fournisseurs importés avec succès');
      handleClose();
      refetch();
      if (retour) navigate(-1);
    } catch (error) {
      const data = error?.data;
      if (data?.erreurs?.length) setErreurs(data.erreurs);
      toast.error(data?.message || 'Fichier non conforme. Veuillez vous référer au modèle.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  const PRIMARY = '#068f7a';
  const PRIMARY_LIGHT = '#f0faf8';
  const PRIMARY_BORDER = '#b2dfd8';

  return (
    <div
      className="modal-overlay"
      onClick={handleClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1050,
        backgroundColor: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          borderRadius: '16px',
          width: '100%',
          maxWidth: '560px',
          maxHeight: '90vh',
          backgroundColor: '#fff',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          boxShadow: '0 20px 60px rgba(0,0,0,0.18)',
        }}
      >
        {/* ── Header fixe ─────────────────────────────────────────────────── */}
        <div style={{
          padding: '14px 20px 12px',
          borderBottom: `1px solid ${PRIMARY_BORDER}`,
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexShrink: 0,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>📥</span>
            <span style={{ fontWeight: 700, fontSize: '0.97rem', color: PRIMARY }}>
              Importer des fournisseurs
            </span>
          </div>
          <button
            type="button"
            onClick={handleClose}
            style={{
              border: 'none', background: 'none', cursor: 'pointer',
              fontSize: 18, color: '#6c757d', lineHeight: 1,
              padding: '2px 6px', borderRadius: 6,
            }}
            aria-label="Fermer"
          >×</button>
        </div>

        {/* ── Zone scrollable (structure + erreurs) ───────────────────────── */}
        <div style={{
          overflowY: 'auto',
          padding: '14px 20px 0',
          flex: '1 1 auto',
        }}>
          {/* Structure attendue */}
          <div style={{
            backgroundColor: PRIMARY_LIGHT,
            border: `1px solid ${PRIMARY_BORDER}`,
            borderRadius: 10,
            padding: '10px 12px',
            marginBottom: 10,
          }}>
            <p style={{ fontSize: '0.8rem', fontWeight: 600, color: PRIMARY, marginBottom: 6 }}>
              📋 Structure attendue du fichier Excel
            </p>
            <p style={{ fontSize: '0.73rem', color: '#555', marginBottom: 8 }}>
              La 1ʳᵉ ligne doit contenir les en-têtes ci-dessous. L&apos;ordre des colonnes n&apos;a pas d&apos;importance.
            </p>

            {/* Badges colonnes */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5, marginBottom: 8 }}>
              {COLONNES.map(({ label, required }) => (
                <span key={label} style={{
                  backgroundColor: required ? PRIMARY : '#6c757d',
                  color: '#fff',
                  borderRadius: 4,
                  padding: '3px 9px',
                  fontSize: '0.72rem',
                  fontWeight: 500,
                }}>
                  {label}{required ? ' *' : ''}
                </span>
              ))}
            </div>

            {/* Tableau compact */}
            <table style={{ width: '100%', fontSize: '0.72rem', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ backgroundColor: '#d9f2ee' }}>
                  {['Colonne', 'Description', 'Format', 'Exemple', '✓'].map((h) => (
                    <th key={h} style={{
                      padding: '4px 6px', textAlign: h === '✓' ? 'center' : 'left',
                      fontWeight: 600, color: '#333',
                      border: '1px solid #c0e0da',
                    }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {COLONNES.map(({ label, required, description, format, example }) => (
                  <tr key={label} style={{ borderBottom: '1px solid #e0f0ed' }}>
                    <td style={{ padding: '4px 6px', fontWeight: 600, color: '#333', border: '1px solid #e0f0ed', whiteSpace: 'nowrap' }}>
                      {label}
                    </td>
                    <td style={{ padding: '4px 6px', color: '#555', border: '1px solid #e0f0ed' }}>
                      {description}
                    </td>
                    <td style={{ padding: '4px 6px', border: '1px solid #e0f0ed' }}>
                      {format
                        ? <span style={{ color: '#c0392b', fontFamily: 'monospace', fontSize: '0.69rem' }}>{format}</span>
                        : <span style={{ color: '#aaa' }}>-</span>
                      }
                    </td>
                    <td style={{ padding: '4px 6px', color: '#777', fontFamily: 'monospace', fontSize: '0.69rem', border: '1px solid #e0f0ed' }}>
                      {example}
                    </td>
                    <td style={{ padding: '4px 6px', textAlign: 'center', border: '1px solid #e0f0ed' }}>
                      {required
                        ? <span style={{ color: PRIMARY, fontWeight: 700 }}>✓</span>
                        : <span style={{ color: '#aaa' }}>-</span>
                      }
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <p style={{ fontSize: '0.68rem', color: '#888', marginTop: 6, marginBottom: 0 }}>
              <span style={{ color: PRIMARY, fontWeight: 700 }}>*</span> Champ obligatoire
            </p>
          </div>

          {/* Erreurs de validation */}
          {erreurs.length > 0 && (
            <div style={{
              backgroundColor: '#fff5f5',
              border: '1px solid #f5c6cb',
              borderRadius: 10,
              padding: '10px 12px',
              marginBottom: 10,
              maxHeight: 130,
              overflowY: 'auto',
            }}>
              <p style={{ fontWeight: 600, color: '#dc3545', fontSize: '0.78rem', marginBottom: 6 }}>
                ⚠️ {erreurs.length} erreur(s) - corrigez votre fichier et réessayez
              </p>
              <ul style={{ margin: 0, paddingLeft: 16, fontSize: '0.75rem', color: '#721c24' }}>
                {erreurs.map((err, i) => <li key={i}>{err}</li>)}
              </ul>
            </div>
          )}
        </div>

        {/* ── Zone upload + actions - toujours visible ─────────────────────── */}
        <div style={{ padding: '12px 20px 16px', flexShrink: 0, borderTop: `1px solid ${PRIMARY_BORDER}` }}>
          <Form onSubmit={handleSubmit} encType="multipart/form-data">
            <Form.Group controlId="fileUploadControl" style={{ marginBottom: selectedFile ? 8 : 12 }}>
              <FileUpload onFileSelect={handleFileSelect} />
            </Form.Group>

            {selectedFile && (
              <div style={{
                display: 'flex', alignItems: 'center', gap: 8,
                backgroundColor: PRIMARY_LIGHT, border: `1px solid ${PRIMARY_BORDER}`,
                borderRadius: 8, padding: '6px 12px', marginBottom: 12,
                fontSize: '0.78rem', color: PRIMARY,
              }}>
                <span>📄</span>
                <span style={{ fontWeight: 600, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {selectedFile.name}
                </span>
                <span style={{ color: '#888', whiteSpace: 'nowrap' }}>
                  ({(selectedFile.size / 1024).toFixed(1)} Ko)
                </span>
              </div>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
              <button
                type="button"
                onClick={handleClose}
                disabled={isLoading}
                style={{
                  border: '1px solid #ccc', background: '#fff', borderRadius: 20,
                  padding: '6px 20px', fontSize: '0.82rem', cursor: 'pointer', color: '#555',
                }}
              >
                Annuler
              </button>
              <button
                type="submit"
                disabled={!selected || isLoading}
                style={{
                  border: 'none',
                  borderRadius: 20,
                  padding: '6px 22px',
                  fontSize: '0.82rem',
                  cursor: selected && !isLoading ? 'pointer' : 'not-allowed',
                  backgroundColor: selected && !isLoading ? PRIMARY : '#a0cfc8',
                  color: '#fff',
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'background-color 0.2s',
                }}
              >
                {isLoading ? (
                  <>
                    <span className="spinner-border spinner-border-sm" role="status" aria-hidden="true" />
                    Importation...
                  </>
                ) : (
                  <>📤 Importer</>
                )}
              </button>
            </div>
          </Form>
        </div>
      </div>
    </div>
  );
};

export default ModalExcel;